import { Button, Flex } from "antd";
import UserImage from "../../../../../assets/images/avatar.svg";
import StatusItem from "../../../../../components/common/StatusItem";
import ConfirmModal from "../../../../../components/ui/ConfirmModal";
import CustomDrawer from "../../../../../components/ui/CustomDrawer";
import { LeaveRequest } from "../../../../../features/organization/contractual/LeaveManagement/leaveManagement.type";
import { useApproveOrRejectLeaveRequestMutation } from "../../../../../features/organization/contractual/LeaveManagement/leaveManagementApi";
import useModal from "../../../../../hooks/useModal";
import { formatDate } from "../../../../../lib/time";
import { toast } from "../../../../../lib/toast";
import { LEAVE_REQUEST_STATUES } from "./LeaveRequests";

interface RequestDetailsProps {
  data?: LeaveRequest;
  open: boolean;
  handleClose: (value?: boolean) => void;
}

const RequestDetails: React.FC<RequestDetailsProps> = ({
  data,
  open,
  handleClose,
}) => {
  const {
    open: cnfirmOpen,
    handleOpen: confirmHandleOpen,
    handleClose: confirmHandleClose,
  } = useModal<any, "CONFIRM_APPROVE" | "CONFIRM_REJECT">();

  const {
    id,
    days_count,
    from_date,
    to_date,
    leaveAllocation,
    status = "pending",
    member,
    business_member_id,
  } = data || {};

  const { leave_name } = leaveAllocation || {};
  const { name: memberName, image: memberImage } = member || {};

  const details = [
    {
      label: "Duration",
      value: `${days_count ?? 0} ${
        days_count && days_count > 1 ? "Days" : "Day"
      }`,
    },
    {
      label: "Date",
      value: `${formatDate(from_date, "DD")} – ${formatDate(to_date)}`,
    },
    {
      label: "Leave Type",
      value: leave_name ?? "n/a",
    },
    {
      label: "Status",
      value: <StatusItem value={status} list={LEAVE_REQUEST_STATUES} />,
    },
  ];

  const [approveOrReject, { isLoading: updating }] =
    useApproveOrRejectLeaveRequestMutation();

  const handleApproveOrReject = async (type: "approved" | "rejected") => {
    try {
      await approveOrReject({
        id: id,
        data: { status: type },
      }).unwrap();
      toast("success", `Leave request ${type} successfully.`);
      handleClose();
      confirmHandleClose();
    } catch (err) {
      console.error(err);
      toast("error", `Leave request ${type} failed`);
    }
  };

  const handleApprove = () => {
    confirmHandleOpen({ type: "CONFIRM_APPROVE" });
  };

  const handleReject = () => {
    confirmHandleOpen({ type: "CONFIRM_REJECT" });
  };

  return (
    <>
      <CustomDrawer
        width={460}
        open={open}
        onClose={handleClose}
        title="Request Details"
      >
        {/* --- User Info --- */}
        <div className="flex items-center gap-4 mb-3">
          <img
            src={memberImage || UserImage}
            alt={`${memberName || "User"} profile`}
            className="w-16 h-16 rounded-full object-cover"
          />
          <div>
            <p className="text-zinc-700 text-xl font-medium">{memberName}</p>
            <p className="text-slate-500 text-sm">
              Bondhu ID: {business_member_id}
            </p>
          </div>
        </div>

        {/* --- Leave Details --- */}
        <div>
          {details.map(({ label, value }, index) => (
            <Flex
              key={index}
              align="center"
              justify="space-between"
              className="border-b border-b-extra-light last:border-none py-4"
            >
              <span className="text-base font-normal text-body-text">
                {label}
              </span>
              <span className="text-base font-normal text-header">{value}</span>
            </Flex>
          ))}
        </div>

        {/* --- Action Buttons --- */}
        {status === "pending" && (
          <Flex align="center" gap={16} className="mt-6">
            <Button
              size="middle"
              htmlType="button"
              className="min-w-[120px] h-10 !bg-green-600 !border-green-600 !text-white"
              onClick={handleApprove}
            >
              Approve
            </Button>
            <Button
              size="middle"
              htmlType="button"
              className="min-w-[120px] h-10 !bg-red-500 !border-red-500 !text-white"
              onClick={handleReject}
            >
              Reject
            </Button>
          </Flex>
        )}
      </CustomDrawer>

      <ConfirmModal
        width={450}
        open={cnfirmOpen?.open && cnfirmOpen.type === "CONFIRM_REJECT"}
        okLoading={updating}
        okText="Reject"
        title="Reject Leave Request?"
        handleClose={() => confirmHandleClose()}
        handleOk={() => handleApproveOrReject("rejected")}
        okButtonProps={{
          className: "!bg-red-500 !border-red-500 !text-white !min-w-[120px]",
          type: "default",
        }}
        subTitle="Are you sure you want to reject this leave request?"
      />

      <ConfirmModal
        width={450}
        open={cnfirmOpen?.open && cnfirmOpen.type === "CONFIRM_APPROVE"}
        okLoading={updating}
        okText="Approve"
        title="Approve Leave Request?"
        handleClose={() => confirmHandleClose()}
        handleOk={() => handleApproveOrReject("approved")}
        okButtonProps={{
          className:
            "!bg-green-600 !text-white !border-green-600 !min-w-[120px]",
          type: "default",
        }}
        subTitle="Are you sure you want to approve this leave request?"
      />
    </>
  );
};

export default RequestDetails;
